import React, { useMemo, useState } from 'react';
import clsx from 'clsx';
import { Archive, CheckCircle2, ChevronDown, ChevronRight, Circle, CircleDashed, Clock3, Sparkles } from 'lucide-react';

// ── Helpers ──────────────────────────────────────────────────────────────────

const parseDate = (str) => {
  if (!str) return null;
  const d = new Date(`${str}T00:00:00`);
  return isNaN(d.getTime()) ? null : d;
};

const formatDate = (d) => {
  if (!d) return 'No date set';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const startOfDay = (d) => {
  const copy = new Date(d);
  copy.setHours(0, 0, 0, 0);
  return copy;
};

const plural = (count, word) => `${count} ${word}${count === 1 ? '' : 's'}`;

// ── Dot icon + colour ─────────────────────────────────────────────────────────

const milestoneStatus = (task, today) => {
  if (task.status === 'completed') return 'completed';
  const due = parseDate(task.due_date);
  if (!due) return 'upcoming';
  return due < today ? 'overdue' : 'upcoming';
};

// ── Today Marker ──────────────────────────────────────────────────────────────

const TodayPill = ({ today }) => (
  <span className="text-xs font-semibold bg-rose-100 text-rose-700 px-2 py-0.5 rounded-full whitespace-nowrap">
    Today — {formatDate(today)}
  </span>
);

// The dashed rule needs width to read as a rule. On a phone it is replaced by
// the bare pill in the header row (see the hero below).
const TodayMarker = ({ today }) => (
  <div className="relative my-2 hidden items-center sm:flex">
    {/* Dot on the line */}
    <div className="absolute left-0 w-5 h-5 flex items-center justify-center" style={{ marginLeft: '-2px' }}>
      <div className="w-2 h-2 rounded-full bg-rose-600 ring-2 ring-rose-200" />
    </div>
    {/* Dashed line + pill */}
    <div className="ml-6 flex items-center gap-3 w-full">
      <div className="flex-1 border-t-2 border-dashed border-rose-400" />
      <TodayPill today={today} />
      <div className="flex-1 border-t-2 border-dashed border-rose-400" />
    </div>
  </div>
);

// ── Main Component ────────────────────────────────────────────────────────────

// Canonical status values -- see TASK_STATUSES in wedding_planner.py.
const statusCopy = (status) => {
  if (status === 'completed') return 'Done';
  if (status === 'in_progress') return 'In progress';
  return 'To decide';
};

// In-progress work gets its own sky palette so the Timeline distinguishes
// "started" from "not touched yet" at a glance. Overdue still wins over it:
// a task being underway does not make a missed date less urgent.
const taskTone = (task, today) => {
  const status = milestoneStatus(task, today);
  if (task.is_milestone) return 'border-rose-200 bg-rose-50/80';
  if (status === 'completed') return 'border-emerald-200 bg-emerald-50/70';
  if (status === 'overdue') return 'border-red-200 bg-red-50/70';
  if (task.status === 'in_progress') return 'border-sky-200 bg-sky-50/70';
  return 'border-stone-200 bg-white/90';
};

// One finished month, folded to a single 44px row. The x/y counts stay on the
// row so the archive still reads as progress rather than as hidden work.
const CollapsedMonthRow = ({ month, onToggle }) => (
  <button
    type="button"
    onClick={onToggle}
    aria-expanded={false}
    className="flex min-h-[44px] w-full items-center gap-3 rounded-2xl border border-emerald-100 bg-emerald-50/60 px-4 py-2 text-left transition hover:bg-emerald-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400"
  >
    <ChevronRight className="h-4 w-4 shrink-0 text-emerald-600" />
    <span className="truncate text-sm font-semibold text-stone-600">{month.label}</span>
    <span className="ml-auto inline-flex shrink-0 items-center gap-1.5 rounded-full bg-white/80 px-2.5 py-1 text-[11px] font-semibold text-emerald-700">
      <CheckCircle2 className="h-3.5 w-3.5" />
      {month.completed_count}/{month.total_count} complete
    </span>
  </button>
);

const TaskCard = ({ task, today, onEditTask }) => {
  const status = milestoneStatus(task, today);
  const dueDate = parseDate(task.due_date);
  const isCompleted = status === 'completed';
  const isInProgress = task.status === 'in_progress' && !isCompleted;
  const handleEdit = () => onEditTask?.(task);
  return (
    <article
      role={onEditTask ? 'button' : undefined}
      tabIndex={onEditTask ? 0 : undefined}
      onClick={onEditTask ? handleEdit : undefined}
      onKeyDown={
        onEditTask
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleEdit();
              }
            }
          : undefined
      }
      className={clsx(
        'rounded-[24px] border p-4 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md',
        onEditTask && 'cursor-pointer focus:outline-none focus:ring-2 focus:ring-rose-300',
        taskTone(task, today)
      )}
    >
      <div className="flex items-start gap-3">
        <div
          className={clsx(
            'mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full',
            isCompleted
              ? 'bg-emerald-100 text-emerald-700'
              : isInProgress
                ? 'bg-sky-100 text-sky-700'
                : task.is_milestone
                  ? 'bg-rose-100 text-rose-700'
                  : 'bg-stone-100 text-stone-500'
          )}
        >
          {isCompleted ? (
            <CheckCircle2 className="h-4 w-4" />
          ) : isInProgress ? (
            // A part-drawn ring: started, but not closed.
            <CircleDashed className="h-4 w-4" />
          ) : (
            <Clock3 className="h-4 w-4" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-semibold uppercase tracking-[0.16em] text-stone-600">
              {formatDate(dueDate)}
            </span>
            <span
              className={clsx(
                'rounded-full px-2 py-0.5 text-[11px] font-semibold',
                isCompleted
                  ? 'bg-emerald-100 text-emerald-700'
                  : isInProgress
                    ? 'bg-sky-100 text-sky-700'
                    : 'bg-white/80 text-stone-500'
              )}
            >
              {statusCopy(task.status)}
            </span>
            {task.owner ? (
              <span className="rounded-full bg-white/80 px-2 py-0.5 text-[11px] font-semibold text-stone-500">
                {task.owner}
              </span>
            ) : null}
          </div>
          <h4 className={clsx('mt-2 text-base font-semibold leading-snug', isCompleted ? 'text-stone-500' : 'text-stone-900')}>
            {task.title}
          </h4>
          {task.description ? (
            <p className="mt-1 text-sm leading-6 text-stone-500">{task.description}</p>
          ) : null}
          {task.notes ? (
            // Completion / audit notes are verbose. They live behind a
            // disclosure so the card stays scannable.
            <details
              className="mt-2"
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
            >
              {/* 44px tall to the finger, ~32px to the layout: the negative
                  margins spend the extra on the card's own bottom padding and
                  the gap above, so the card does not grow around it. */}
              <summary className="-mt-1 -mb-2 inline-flex min-h-[44px] cursor-pointer list-none items-center gap-1 rounded-full bg-white/80 px-2.5 py-1 text-[11px] font-semibold text-stone-500 ring-1 ring-stone-200 hover:text-stone-700">
                Details
              </summary>
              <p className="mt-2 rounded-2xl bg-white/70 px-3 py-2 text-xs leading-5 text-stone-500 ring-1 ring-stone-200">
                {task.notes}
              </p>
            </details>
          ) : null}
        </div>
      </div>
    </article>
  );
};

// A month opened out in full: heading, x/y progress, and every task card.
const MonthDetail = ({ month, today, onEditTask, isCurrentFocus, isArchived, onCollapse }) => (
  <>
    {/* Label and progress share a line so the first task card sits as high as
        possible — on a phone every row above it costs a slice of the fold. */}
    <div className="mb-2 flex flex-wrap items-center justify-between gap-x-3 gap-y-1 sm:mb-3">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        {isArchived ? (
          <button
            type="button"
            onClick={onCollapse}
            aria-expanded
            className="inline-flex min-h-[44px] items-center gap-1.5 rounded-xl text-xl font-semibold text-stone-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400"
          >
            <ChevronDown className="h-4 w-4 text-emerald-600" />
            {month.label}
          </button>
        ) : (
          <h3 className="text-xl font-semibold text-stone-900">{month.label}</h3>
        )}
        <p className="text-xs uppercase tracking-[0.18em] text-stone-600">
          {month.completed_count}/{month.total_count} complete
        </p>
      </div>
      {isCurrentFocus ? (
        <span className="rounded-full bg-rose-100 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-rose-700">
          Current focus
        </span>
      ) : null}
    </div>
    <div className="grid gap-3">
      {month.tasks.map((task) => (
        <TaskCard key={task.id} task={task} today={today} onEditTask={onEditTask} />
      ))}
    </div>
  </>
);

const WeddingTimeline = ({ board, onEditTask }) => {
  const today = useMemo(() => startOfDay(new Date()), []);
  // Every finished month that sits before the current focus is folded into a
  // single archive disclosure. Before this, each finished month kept its own
  // row, and on a 390px phone six of those rows pushed the current month's
  // first task ~450px below the fold. One row keeps the archive one tap away
  // while current work starts inside the first screenful.
  const [archiveOpen, setArchiveOpen] = useState(null); // null = auto
  const [expandedMonths, setExpandedMonths] = useState(() => new Set());

  const toggleMonth = (month) =>
    setExpandedMonths((prev) => {
      const next = new Set(prev);
      if (next.has(month)) next.delete(month);
      else next.add(month);
      return next;
    });

  const months = useMemo(() => {
    return (board?.months ?? []).map((month) => ({
      ...month,
      tasks: [...(month.tasks ?? [])].sort((a, b) => {
        const da = parseDate(a.due_date);
        const db = parseDate(b.due_date);
        if (!da && !db) return 0;
        if (!da) return 1;
        if (!db) return -1;
        return da - db;
      }),
    }));
  }, [board]);

  const firstOpenMonthIndex = months.findIndex((month) =>
    (month.tasks ?? []).some((task) => task.status !== 'completed')
  );

  // Everything before the current focus is, by definition, fully done.
  const archivedMonths = firstOpenMonthIndex === -1 ? months : months.slice(0, firstOpenMonthIndex);
  const liveMonths = firstOpenMonthIndex === -1 ? [] : months.slice(firstOpenMonthIndex);
  const archivedTaskCount = archivedMonths.reduce((sum, m) => sum + (m.completed_count ?? 0), 0);
  // With nothing left to work on, the archive *is* the timeline, so open it.
  const isArchiveOpen = archiveOpen === null ? liveMonths.length === 0 : archiveOpen;

  if (months.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-[30px] bg-white/75 py-24 text-center shadow-sm ring-1 ring-stone-200">
        <Circle className="mb-4 h-10 w-10 text-stone-500" />
        <p className="max-w-xs text-sm text-stone-500">
          No planning cards yet.
        </p>
      </div>
    );
  }

  const archiveSummary =
    archivedMonths.length === 1
      ? `${plural(archivedTaskCount, 'completed task')} in ${archivedMonths[0].label}`
      : `${plural(archivedTaskCount, 'completed task')} across ${plural(archivedMonths.length, 'month')}`;
  const archiveRange =
    archivedMonths.length > 1
      ? `${archivedMonths[0].label} – ${archivedMonths[archivedMonths.length - 1].label}`
      : null;

  return (
    <div className="space-y-4 pb-8">
      <section className="overflow-hidden rounded-[32px] border border-white/70 bg-white/80 p-4 shadow-[0_22px_60px_rgba(111,78,55,0.12)] ring-1 ring-stone-200 backdrop-blur sm:p-5">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.24em] text-rose-700">
            <Sparkles className="h-3.5 w-3.5" />
            Timeline first
          </div>
          <span className="sm:hidden">
            <TodayPill today={today} />
          </span>
        </div>
        <h2 className="font-wedding-display mt-2 text-3xl font-medium leading-none text-stone-900 sm:mt-3 sm:text-5xl">
          The road to Singapore
        </h2>
        {/* The intro is context, not navigation — on a phone the screen is
            better spent on the month you are actually working on. */}
        <p className="mt-3 hidden text-sm leading-6 text-stone-500 sm:block">
          Month-by-month planning cards. Finished months fold into the archive so the current month stays at the top of
          the scroll.
        </p>
        <TodayMarker today={today} />
      </section>

      <div className="relative">
        <div className="absolute bottom-0 left-5 top-0 w-px bg-gradient-to-b from-rose-200 via-stone-200 to-transparent" />
        <div className="space-y-5">
          {archivedMonths.length > 0 ? (
            <section className="relative pl-12">
              <div className="absolute left-[15px] top-[15px] h-2.5 w-2.5 rounded-full bg-emerald-600 ring-4 ring-[#f7efe8]" />
              <button
                type="button"
                onClick={() => setArchiveOpen(!isArchiveOpen)}
                aria-expanded={isArchiveOpen}
                aria-controls="timeline-archive"
                className="flex min-h-[44px] w-full items-center gap-2.5 rounded-2xl border border-emerald-100 bg-emerald-50/60 px-4 py-2 text-left transition hover:bg-emerald-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400"
              >
                {isArchiveOpen ? (
                  <ChevronDown className="h-4 w-4 shrink-0 text-emerald-600" />
                ) : (
                  <ChevronRight className="h-4 w-4 shrink-0 text-emerald-600" />
                )}
                <Archive className="hidden h-4 w-4 shrink-0 text-emerald-600 sm:block" />
                <span className="truncate text-[13px] font-semibold text-stone-600 sm:text-sm">{archiveSummary}</span>
                {archiveRange ? (
                  <span className="ml-auto hidden shrink-0 rounded-full bg-white/80 px-2.5 py-1 text-[11px] font-semibold text-emerald-700 sm:inline">
                    {archiveRange}
                  </span>
                ) : null}
              </button>

              {isArchiveOpen ? (
                <div id="timeline-archive" className="mt-3 space-y-4 border-l border-dashed border-emerald-200 pl-4">
                  {archivedMonths.map((month) => {
                    const isExpanded = expandedMonths.has(month.month);
                    return (
                      <div key={month.month}>
                        {isExpanded ? (
                          <MonthDetail
                            month={month}
                            today={today}
                            onEditTask={onEditTask}
                            isArchived
                            onCollapse={() => toggleMonth(month.month)}
                          />
                        ) : (
                          <CollapsedMonthRow month={month} onToggle={() => toggleMonth(month.month)} />
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : null}
            </section>
          ) : null}

          {liveMonths.map((month, index) => (
            <section key={month.month} className="relative pl-12">
              <div
                className={clsx(
                  'absolute left-[13px] top-2 flex h-4 w-4 items-center justify-center rounded-full ring-4 ring-[#f7efe8]',
                  index === 0 ? 'bg-rose-600' : 'bg-stone-500'
                )}
              />
              <MonthDetail
                month={month}
                today={today}
                onEditTask={onEditTask}
                isCurrentFocus={index === 0}
              />
            </section>
          ))}
        </div>
      </div>
    </div>
  );
};

export default WeddingTimeline;
